import {
  usePrinters,
  usePrinterStatusEvents,
  useRegisterPrinter,
  useTestPrint,
  useUpdatePrinter,
} from '@/features/print/hooks/usePrint';
import { useT } from '@/i18n';
import type { PrinterStatusEvent } from '@restaurant/api-client';
import {
  Block,
  BlockFooter,
  BlockTitle,
  List,
  ListButton,
  ListInput,
  ListItem,
  NavLeft,
  NavRight,
  NavTitle,
  Navbar,
  Link as F7Link,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useEffect, useState } from 'react';
import {
  NOTIFICATION_URL,
  PRINT_URL,
  copyRow,
} from '@/features/print/components/serverDirectPrint';

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

const eventTime = (iso: string) =>
  new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

// One timeline row. Function-returns-ListItem so F7 keeps it inside the <ul>.
// A plain (non-media) list vertically centres media + title + after, so the
// common single-line rows (Connected / Offline) sit centred against the icon;
// `text` carries the optional error line without top-anchoring the row.
function statusEventRow(
  e: PrinterStatusEvent,
  index: number,
  eventMeta: Record<PrinterStatusEvent['status'], { label: string; icon: string; color: string }>,
) {
  const meta = eventMeta[e.status];
  return (
    <ListItem
      key={`${e.at}-${index}`}
      title={meta.label}
      after={eventTime(e.at)}
      text={e.status === 'ERROR' ? (e.errorText ?? undefined) : undefined}
    >
      <i slot="media" className="f7-icons" style={{ color: meta.color, fontSize: 20 }}>
        {meta.icon}
      </i>
    </ListItem>
  );
}

/**
 * Add / edit a printer. This used to be a PushSheet inside PrintersPage; it is
 * now a classic pushed router page (Rule 12: a self-contained edit flow slides
 * in as a full-width page, not a bottom sheet - the push-transform sheet was
 * fragile inside the Settings nested master-detail view). Reached from the
 * Printers list via `/settings/printers/new` and `/settings/printers/:printerId`.
 *
 * The operator sets a Name (+ Active / Default). The pairing "Printer ID" is
 * normally generated by the backend - shown read-only in edit mode to copy into
 * the printer's Server-Direct-Print setup. When adding, the operator may instead
 * paste an existing device's id to adopt an Epson already configured on the old
 * system (migration), so it keeps printing without being re-paired.
 */
export default function PrinterEditFormPage({
  f7route,
  f7router,
}: {
  f7route: { params: { printerId?: string } };
  f7router: { back: () => void };
}) {
  const t = useT();
  const eventMeta: Record<
    PrinterStatusEvent['status'],
    { label: string; icon: string; color: string }
  > = {
    ONLINE: { label: t.print.eventConnected, icon: 'checkmark_alt_circle_fill', color: '#34c759' },
    OFFLINE: { label: t.print.statusOffline, icon: 'minus_circle_fill', color: '#8e8e93' },
    ERROR: { label: t.print.statusError, icon: 'exclamationmark_triangle_fill', color: '#ff3b30' },
  };
  const { printerId } = f7route.params;
  const { data: printers } = usePrinters();
  const register = useRegisterPrinter();
  const update = useUpdatePrinter();
  const testPrint = useTestPrint();

  const printer = printerId ? printers?.find((p) => p.id === printerId) : null;
  const isEdit = Boolean(printerId);
  const { data: statusEvents } = usePrinterStatusEvents(printer?.id ?? null);

  const [name, setName] = useState('');
  // Add mode only: an id pasted from a device already set up on the old system.
  // Blank → the backend generates a fresh one. Must match the contract's shape.
  const [adoptedId, setAdoptedId] = useState('');
  const [active, setActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [seeded, setSeeded] = useState(false);

  // Seed the form once the printer resolves (the list may load after a deep
  // link). Add mode (no id) keeps the defaults above.
  useEffect(() => {
    if (seeded || !isEdit || !printer) return;
    setName(printer.name);
    setActive(printer.isActive);
    setIsDefault(printer.isDefault);
    setSeeded(true);
  }, [seeded, isEdit, printer]);

  const back = () => f7router.back();

  // Test print: the ticket is queued here, but it only reaches paper when the
  // device next polls - so the toast promises "in a few seconds", not "printed".
  const testing = testPrint.isPending;
  const runTestPrint = () => {
    if (!printer) return;
    testPrint.mutate(printer.id, {
      onSuccess: () => toast(t.print.testPrintQueued),
      onError: (e: unknown) => toast(e instanceof Error ? e.message : t.print.testPrintFailed),
    });
  };

  const submit = () => {
    const n = name.trim();
    if (!n) return toast(t.print.nameRequired);
    const onSuccess = () => {
      toast(isEdit ? t.print.printerUpdated : t.print.printerAdded);
      back();
    };
    const onError = (e: unknown) => toast(e instanceof Error ? e.message : t.print.saveFailed);

    if (printer) {
      update.mutate(
        { printerId: printer.id, data: { name: n, isActive: active, isDefault } },
        { onSuccess, onError },
      );
    } else {
      const id = adoptedId.trim();
      if (id && !/^[A-Za-z0-9]{8,16}$/.test(id)) {
        return toast(t.print.idFormatInvalid);
      }
      register.mutate(
        {
          name: n,
          type: 'EPSON_SERVER_DIRECT',
          deviceId: id || undefined,
          isActive: active,
          isDefault,
        },
        { onSuccess, onError },
      );
    }
  };

  // Deep-linked an edit before the list loaded, or an unknown id.
  const loading = isEdit && !printers;
  const missing = isEdit && !loading && !printer;
  const ready = !loading && !missing;
  const saving = register.isPending || update.isPending;

  return (
    <Page name="printer-edit">
      <Navbar>
        <NavLeft>
          <F7Link onClick={back}>{t.cancel}</F7Link>
        </NavLeft>
        <NavTitle>{isEdit ? t.print.editPrinterTitle : t.print.addPrinterTitle}</NavTitle>
        <NavRight>
          <F7Link onClick={ready && !saving ? submit : undefined}>{t.saveShort}</F7Link>
        </NavRight>
      </Navbar>

      {loading && (
        <Block className="text-align-center" style={{ paddingTop: 48 }}>
          <Preloader />
        </Block>
      )}

      {missing && (
        <Block strongIos strongMd outlineIos className="text-align-center">
          <p>{t.print.printerGone}</p>
        </Block>
      )}

      {ready && (
        <>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={t.print.nameLabel}
              type="text"
              placeholder={t.print.namePlaceholder}
              value={name}
              outline={f7.theme !== 'ios'}
              floatingLabel={f7.theme !== 'ios'}
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
            />
          </List>

          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title={t.print.activeLabel}>
              <ToggleSwitch slot="after" checked={active} onChange={setActive} />
            </ListItem>
            <ListItem title={t.print.defaultPrinterLabel}>
              <ToggleSwitch slot="after" checked={isDefault} onChange={setIsDefault} />
            </ListItem>
          </List>
          <BlockFooter>{t.print.defaultPrinterFooter}</BlockFooter>

          {!printer && (
            <>
              <BlockTitle>{t.print.migratingTitle}</BlockTitle>
              <BlockFooter style={{ marginTop: -6 }}>{t.print.migratingFooter}</BlockFooter>
              <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                <ListInput
                  label={t.print.existingIdLabel}
                  type="text"
                  placeholder={t.print.existingIdPlaceholder}
                  value={adoptedId}
                  outline={f7.theme !== 'ios'}
                  floatingLabel={f7.theme !== 'ios'}
                  onInput={(e) => setAdoptedId((e.target as HTMLInputElement).value)}
                />
              </List>
            </>
          )}

          {printer && (
            <>
              <BlockTitle>{t.print.printerSetupTitle}</BlockTitle>
              <BlockFooter style={{ marginTop: -6 }}>{t.print.printerSetupFooter}</BlockFooter>
              <List strongIos strongMd insetIos insetMd dividersIos dividersMd mediaList>
                {copyRow(t.print, t.print.printerIdLabel, printer.connection.deviceId)}
                {PRINT_URL && copyRow(t.print, t.print.printUrlLabel, PRINT_URL)}
                {NOTIFICATION_URL &&
                  copyRow(t.print, t.print.notificationUrlLabel, NOTIFICATION_URL)}
              </List>

              <List strongIos strongMd insetIos insetMd>
                <ListButton
                  className={testing ? 'disabled' : undefined}
                  onClick={testing ? undefined : runTestPrint}
                >
                  {testing ? t.print.testPrintSending : t.print.testPrintButton}
                </ListButton>
              </List>
              <BlockFooter style={{ marginTop: -6 }}>{t.print.testPrintFooter}</BlockFooter>

              <BlockTitle>{t.print.statusHistoryTitle}</BlockTitle>
              {statusEvents && statusEvents.length > 0 ? (
                <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                  {statusEvents.map((e, i) => statusEventRow(e, i, eventMeta))}
                </List>
              ) : (
                <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                  <ListItem title={t.print.noStatusEvents} footer={t.print.noStatusEventsFooter} />
                </List>
              )}
            </>
          )}
        </>
      )}
    </Page>
  );
}
